import React, { useState, useEffect } from 'react';
import { ErrorDisplay } from './ErrorDisplay';
import * as styles from './ErrorNotification.css.ts';
import logger from '../utils/logger';

interface ErrorInfo {
  id: string;
  error: Error;
  timestamp: number;
}

export const ErrorNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [errors, setErrors] = useState<ErrorInfo[]>([]);

  useEffect(() => {
    // グローバルエラーハンドラーを設定
    const handleError = (event: ErrorEvent) => {
      const error = new Error(event.message);
      error.stack = event.error?.stack || `at ${event.filename}:${event.lineno}:${event.colno}`;

      // loggerにも記録
      logger.error('[ErrorNotification] Uncaught error:', error);

      const errorInfo: ErrorInfo = {
        id: Date.now().toString(),
        error,
        timestamp: Date.now()
      };

      setErrors(prev => [...prev, errorInfo]);
    };

    // 未処理のPromiseリジェクションをキャッチ
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = new Error(
        event.reason?.message ||
        (typeof event.reason === 'string' ? event.reason : 'Unhandled Promise Rejection')
      );
      error.stack = event.reason?.stack || 'No stack trace available';

      // loggerにも記録
      logger.error('[ErrorNotification] Unhandled promise rejection:', error, event.reason);

      const errorInfo: ErrorInfo = {
        id: Date.now().toString(),
        error,
        timestamp: Date.now()
      };

      setErrors(prev => [...prev, errorInfo]);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // logger.errorへのフックを追加
    const originalLoggerError = window.console.error;
    window.console.error = (...args: unknown[]) => {
      originalLoggerError.apply(console, args);
      
      // エラーオブジェクトを探す
      let error: Error | null = null;
      for (const arg of args) {
        if (arg instanceof Error) {
          error = arg;
          break;
        }
      }
      
      // エラーオブジェクトがない場合は作成
      if (!error) {
        error = new Error(args.map(a => String(a)).join(' '));
      }
      
      const errorInfo: ErrorInfo = {
        id: Date.now().toString() + Math.random(),
        error,
        timestamp: Date.now()
      };

      // レンダリング中にconsole.errorが呼ばれた場合、setStateを非同期にスケジュール
      // これによりReactの「Cannot update a component while rendering」警告を回避
      queueMicrotask(() => {
        setErrors(prev => [...prev, errorInfo]);
      });
    };

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.console.error = originalLoggerError;
    };
  }, []);

  const handleDismiss = (id: string) => {
    setErrors(prev => prev.filter(e => e.id !== id));
  };

  const handleDismissAll = () => {
    setErrors([]);
  };

  return (
    <>
      {children}
      {errors.length > 0 && (
        <div
          className={styles.notificationContainer}
        >
          <div className={styles.header}>
            <h3 className={styles.title}>Errors ({errors.length})</h3>
            <button 
              className={styles.dismissAllButton}
              onClick={handleDismissAll}
            >
              Dismiss All
            </button>
          </div>
          <div className={styles.errorList}>
            {errors.map(errorInfo => (
              <div key={errorInfo.id} className={styles.errorItem}>
                <button
                  className={styles.dismissButton}
                  onClick={() => handleDismiss(errorInfo.id)}
                  title="Dismiss this error"
                >
                  ✕
                </button>
                <ErrorDisplay error={errorInfo.error} showStack={true} />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};